import React, { useState, useEffect } from 'react';
import { Users, UserCheck, Calendar, Clock, TrendingUp, Activity, Heart, Plus, ArrowRight, AlertCircle, Star, Zap, BarChart3, RefreshCw } from 'lucide-react';
import { dashboardAPI } from '../services/api';


const Dashboard = ({ onNavigateToModule }) => {
  const [stats, setStats] = useState({
    totalPatients: 0, todayVisits: 0, pendingTokens: 0, activeQueue: 0, emergencyToday: 0,
    thisMonth: { registrations: 0, visits: 0, revenue: 0, avgDailyVisits: 0 }
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { loadDashboardData(); }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Using getOverview() - must match api.js
      const [overviewData, activityData] = await Promise.all([
        dashboardAPI.getOverview(),
        dashboardAPI.getRecentActivity(10)
      ]);
      setStats({
        totalPatients: overviewData.totalPatients || 0,
        todayVisits: overviewData.todayVisits || 0,
        pendingTokens: overviewData.pendingTokens || 0,
        activeQueue: overviewData.activeQueue || 0,
        emergencyToday: overviewData.emergencyToday || 0,
        thisMonth: {
          registrations: overviewData.thisMonth?.registrations || 0,
          visits: overviewData.thisMonth?.visits || 0,
          revenue: overviewData.thisMonth?.revenue || 0,
          avgDailyVisits: overviewData.thisMonth?.avgDailyVisits || 0
        }
      });
      setRecentActivity(activityData || []);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data. Please check if backend is running.');
    } finally {
      setLoading(false);
    }
  };

  // Quick actions share the single emerald brand color (per the design system) — they're
  // navigation shortcuts distinguished by icon + label, not by category, so rainbow tiles
  // would read as decorative noise.
  const quickActions = [
    { id: 'register', title: 'Register Patient', icon: <Users className="h-6 w-6" />, color: 'from-emerald-500 to-teal-600', module: 'patients' },
    { id: 'token', title: 'Issue Token', icon: <Plus className="h-6 w-6" />, color: 'from-emerald-500 to-teal-600', module: 'tokens' },
    { id: 'queue', title: 'View Queue', icon: <Clock className="h-6 w-6" />, color: 'from-emerald-500 to-teal-600', module: 'tokens' },
    { id: 'family', title: 'Family Management', icon: <Heart className="h-6 w-6" />, color: 'from-emerald-500 to-teal-600', module: 'family' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
              <BarChart3 className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600">Overview and quick actions</p>
            </div>
          </div>
          <button onClick={loadDashboardData} disabled={loading} className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
            <div className="flex items-center"><AlertCircle className="h-5 w-5 text-red-500 mr-2" /><p className="text-red-700">{error}</p></div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard title="Total Patients" value={stats.totalPatients.toLocaleString()} icon={<Users className="h-6 w-6 text-white" />} gradient="from-blue-500 to-indigo-600" change={`${stats.thisMonth.registrations} new this month`} loading={loading} />
          <StatCard title="Today's Visits" value={stats.todayVisits} icon={<Calendar className="h-6 w-6 text-white" />} gradient="from-emerald-500 to-teal-600" change={`${stats.emergencyToday} emergency`} loading={loading} />
          <StatCard title="Waiting Queue" value={stats.pendingTokens} icon={<Clock className="h-6 w-6 text-white" />} gradient="from-amber-500 to-orange-600" change={`${stats.activeQueue} in progress`} loading={loading} />
          <StatCard title="Monthly Revenue" value={`₨${(stats.thisMonth.revenue / 1000).toFixed(1)}K`} icon={<TrendingUp className="h-6 w-6 text-white" />} gradient="from-emerald-500 to-green-600" change={`${stats.thisMonth.visits} visits`} loading={loading} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex items-center justify-center"><Zap className="h-5 w-5 text-white" /></div>
                <h3 className="text-lg font-bold text-gray-800">Quick Actions</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {quickActions.map((action) => (
                  <button key={action.id} onClick={() => onNavigateToModule && onNavigateToModule(action.module)} className="group p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-xl transition-all text-left">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center text-white`}>{action.icon}</div>
                      <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">{action.title}</h3>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex items-center justify-center"><Activity className="h-5 w-5 text-white" /></div>
              <h3 className="text-lg font-bold text-gray-800">Recent Activity</h3>
            </div>
            <div className="space-y-2">
              {loading ? Array.from({ length: 5 }).map((_, i) => <div key={i} className="flex items-center space-x-3 p-3"><div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div><div className="flex-1"><div className="w-24 h-4 bg-gray-200 rounded animate-pulse mb-1"></div></div></div>)
                : recentActivity.length > 0 ? recentActivity.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50">
                    <div className={`w-8 h-8 ${activity.type === 'registration' ? 'bg-blue-100' : 'bg-emerald-100'} rounded-lg flex items-center justify-center`}>
                      {activity.type === 'registration' ? <Users className="h-4 w-4 text-blue-600" /> : <UserCheck className="h-4 w-4 text-emerald-600" />}
                    </div>
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium text-gray-800 truncate">{activity.patient}</div><div className="text-xs text-gray-500">{activity.mrn}</div></div>
                    <div className="text-xs text-gray-400">{activity.time}</div>
                  </div>
                )) : <div className="text-center py-8 text-gray-500"><Activity className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>No recent activity</p></div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, gradient, change, loading }) => (
  <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 hover:shadow-lg transition-all">
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="text-gray-500 text-sm mb-1">{title}</div>
        {loading ? <div className="w-20 h-8 bg-gray-200 rounded animate-pulse"></div> : <div className="text-3xl font-bold text-gray-900">{value}</div>}
        {loading ? <div className="w-24 h-3 bg-gray-200 rounded animate-pulse mt-2"></div> : <div className="text-xs text-gray-500 mt-1">{change}</div>}
      </div>
      <div className={`w-12 h-12 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center flex-shrink-0`}>{icon}</div>
    </div>
  </div>
);

export default Dashboard;